import { useState } from "react";

import { useList } from "@refinedev/core";
import { keepPreviousData } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useDebounce } from "use-debounce";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useEventGroupRepository } from "@/pages/event-groups/hooks/use-event-group-repository";
import {
  SEARCH_DEBOUNCE_MS,
  emptyMessage,
  groupCountLabel,
  groupExploreMeta,
} from "@/pages/event-groups/lib/explore";
import { refusalNamingEvent } from "@/pages/event-groups/lib/refusal-message";
import type { EventGroupHit } from "@/types/event-group";

export type AddToGroupDialogProps = {
  eventId: string;
  /** The event's name, which a refusal that names the event is shown with. */
  eventLabel?: string;
  /** The page holds unsaved edits that a re-read would drop, so the group stays as it is for now. */
  disabled?: boolean;
  /** The add went through: the page re-reads the event. */
  onAdded: () => void;
};

const ADD_FAILED =
  "Could not add the event to the group. Try again in a moment.";

/** Groups a page of the picker shows. A dialog list stays short enough to read without scrolling far. */
const PICKER_PAGE_SIZE = 10;

/**
 * The groups, one page at a time, each with an Add. It is mounted only while the dialog is open, so
 * every opening starts from page 1 with an empty search and no stale refusal. The list is the
 * default provider's `getList` over `/event-groups/explore`, so the name box searches every group
 * and the pager counts from the total, as the groups table does.
 */
function GroupPicker({
  eventId,
  eventLabel,
  onAdded,
  onAddingChange,
}: Pick<AddToGroupDialogProps, "eventId" | "eventLabel" | "onAdded"> & {
  /** An add is running: the dialog must not close under it. */
  onAddingChange: (adding: boolean) => void;
}) {
  const repo = useEventGroupRepository();
  const [typed, setTyped] = useState("");
  const [debounced] = useDebounce(typed, SEARCH_DEBOUNCE_MS);
  const term = debounced.trim();
  // The page belongs to the term it was picked under. A new term starts at page 1 in the same
  // render, so the new term is never asked for the old page and an old term never gets its page back.
  const [paging, setPaging] = useState({ term: "", page: 1 });
  if (paging.term !== term) setPaging({ term, page: 1 });
  const page = paging.term === term ? paging.page : 1;
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { result, query } = useList<EventGroupHit>({
    resource: "event-groups",
    pagination: { currentPage: page, pageSize: PICKER_PAGE_SIZE },
    sorters: [{ field: "updatedAt", order: "desc" }],
    meta: groupExploreMeta(term),
    // The rows stay up while the next page loads, so the pager does not flicker to page 1 of 1.
    queryOptions: { placeholderData: keepPreviousData },
    // A failed load shows below as its own message with a Retry; a toast on top would say it twice.
    errorNotification: false,
  });

  const rows = result.data;
  const total = result.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PICKER_PAGE_SIZE));
  const emptyText = emptyMessage(rows.length, total, term);

  async function add(groupId: string) {
    setAddingId(groupId);
    onAddingChange(true);
    setError(null);
    try {
      const result = await repo.addEvents(groupId, [eventId]);
      if (!result.ok) {
        // The backend's sentence as it is: a full group and an unknown event read differently.
        setError(
          refusalNamingEvent(result.message, [
            { id: eventId, label: eventLabel ?? "" },
          ]),
        );
        setAddingId(null);
        onAddingChange(false);
        return;
      }
      // The dialog closes now, and its picker unmounts with it, so the add need not clear.
      onAddingChange(false);
      onAdded();
    } catch (caught) {
      setError(toHumanErrorMessage(caught) || ADD_FAILED);
      setAddingId(null);
      onAddingChange(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search groups"
          placeholder="Search by name"
          className="w-56"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <span className="text-xs text-muted-foreground">
          {groupCountLabel(total)}
        </span>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {query.isError ? (
        <div className="flex items-center gap-3 text-sm text-destructive">
          <span>
            {toHumanErrorMessage(query.error) ||
              "Could not load the event groups."}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetching}
            onClick={() => query.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : query.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : emptyText ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="flex max-h-72 flex-col divide-y overflow-y-auto rounded-md border">
          {rows.map(({ document: group }) => (
            <li
              key={group.id}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <span className="flex min-w-0 items-center gap-2 text-sm">
                <span className="truncate">{group.name}</span>
                {group.hasExternalKey && (
                  <Badge variant="secondary">Tina</Badge>
                )}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                // The rows of the last search stay up while a new one loads: not to be picked from.
                disabled={addingId !== null || query.isPlaceholderData}
                onClick={() => void add(group.id)}
              >
                Add
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-end gap-2">
        <span className="text-sm text-muted-foreground">
          Page {page} of {pageCount}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page <= 1 || query.isPlaceholderData}
          onClick={() => setPaging({ term, page: page - 1 })}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={page >= pageCount || query.isPlaceholderData}
          onClick={() => setPaging({ term, page: page + 1 })}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/**
 * The Add to a group button of an event that has no group, and the picker behind it. An add the
 * backend refuses (a full group, an event another group holds) shows the backend's message and
 * leaves the picker open; one that goes through closes it and tells the page to re-read the event.
 */
export function AddToGroupDialog({
  eventId,
  eventLabel,
  disabled = false,
  onAdded,
}: AddToGroupDialogProps) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // An add in flight answers later, and the picker must still be there to show it.
        if (!adding) setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled}>
          <Plus className="size-4" />
          Add to a group
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add this event to a group</DialogTitle>
          <DialogDescription>
            Pick the group. An event belongs to one group at a time.
          </DialogDescription>
        </DialogHeader>
        <GroupPicker
          eventId={eventId}
          eventLabel={eventLabel}
          onAddingChange={setAdding}
          onAdded={() => {
            setOpen(false);
            onAdded();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
